// SETTINGS V2 — pure validation for the guest-pass / demo-user mint form (split
// out of IssueGuestPassDialogV2 for the 300-line rule). No React, no i18n
// instance: translated strings come in as already-resolved callbacks so this
// stays testable and the dialog stays presentational.
import { emailRegex } from "../../../../shared/validators";

export interface GuestPassFormValues {
  machineInternalNumber: string;
  guestEmail: string;
  guestName: string;
  duration: string;
  visitPurpose: string;
}

export interface GuestPassFormErrors {
  machine?: string;
  guestEmail?: string;
  guestName?: string;
  visitPurpose?: string;
}

export interface BuildErrorsArgs {
  values: GuestPassFormValues;
  touched: Record<string, boolean>;
  visitPurposeMax: number;
  /** common:forms.required */
  requiredText: string;
  /** account:invalidEmail */
  invalidEmailText: string;
  /** common:forms.maxLength with {max} already interpolated */
  maxLengthText: string;
}

export const buildGuestPassFormErrors = ({
  values,
  touched,
  visitPurposeMax,
  requiredText,
  invalidEmailText,
  maxLengthText,
}: BuildErrorsArgs): GuestPassFormErrors => {
  const emailValid = emailRegex.test(values.guestEmail);

  let guestEmail: string | undefined;
  if (touched.guestEmail) {
    if (values.guestEmail.trim() === "") guestEmail = requiredText;
    else if (!emailValid) guestEmail = invalidEmailText;
  }

  return {
    machine:
      touched.machineInternalNumber && !values.machineInternalNumber
        ? requiredText
        : undefined,
    guestEmail,
    guestName:
      touched.guestName && values.guestName.trim() === "" ? requiredText : undefined,
    visitPurpose:
      touched.visitPurpose && values.visitPurpose.length > visitPurposeMax
        ? maxLengthText
        : undefined,
  };
};

/** Submit gate — identical for both pass types.
 *
 *  This briefly carried an extra demo-only requirement: an acknowledgement that
 *  the PIN was being emailed to the operator's own inbox rather than the
 *  visitor's. Demo passes now send NO email at all (owner ruling 2026-08-04), so
 *  the hazard that justified that gate no longer exists and the gate is gone with
 *  it — do not reintroduce it without a delivery channel to protect. */
export const isGuestPassFormValid = (
  values: GuestPassFormValues,
  visitPurposeMax: number
): boolean =>
  !!values.machineInternalNumber &&
  !!values.guestName.trim() &&
  emailRegex.test(values.guestEmail) &&
  values.visitPurpose.length <= visitPurposeMax;
